import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Paper,
  Grid,
  Divider,
  Chip,
  Button,
  Tab,
  Tabs,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  LinearProgress,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  CalendarToday as CalendarIcon,
  MedicalServices as MedicalIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  CloudUpload as CloudUploadIcon,
  InsertDriveFile as FileIcon,
  Image as ImageIcon,
  PictureAsPdf as PdfIcon,
  Description as DocumentIcon,
  Download as DownloadIcon,
  Print as PrintIcon,
  Visibility as ViewIcon,
  Close as CloseIcon,
  OpenInFull as FullscreenIcon,
  CloseFullscreen as ExitFullscreenIcon,
  Send as SendIcon,
  Message as MessageIcon,
  LocalPharmacy as PharmacyIcon,
  AttachMoney as MoneyIcon,
  FileDownload as FileDownloadIcon,
  Payment as PaymentIcon,
  ContentCopy as ContentCopyIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import axios from 'axios';
import { checkTokenValidity, forceTokenRefresh } from '../../utils/tokenRefresh';

// Get API URL from environment variables
import { API_URL } from '../../utils/apiConfig';
import { formatCurrencySync } from '../../utils/currencyUtils';
import { downloadTreatmentPDF, openTreatmentPDF } from '../../utils/pdfTreatment';
import { downloadTreatmentPlanPDF, openTreatmentPlanPDF } from '../../utils/pdfTreatmentPlan';
import { downloadInvoicePDF, openInvoicePDF } from '../../utils/pdfInvoice';
import { downloadInvoiceCSV } from '../../utils/invoiceUtils';
import { formatPhoneForDisplay } from '../../utils/phoneUtils';
import intakeAPI from '../../api/intake';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import {
  SESSION_EXPIRED_MESSAGE,
  formatFailedActionMessage,
  formatSuccessActionMessage,
} from '../../utils/uiMessages';
import { getApiErrorMessage } from '../../utils/errorUtils';

// All patient data will be fetched from API








// Communications will be fetched from API

// Helper function to safely format dates
const safeFormatDate = (dateValue, formatString = 'dd MMM yyyy') => {
  if (!dateValue) return 'Not available';
  try {
    const date = new Date(dateValue);
    if (isNaN(date.getTime())) return 'Invalid date';
    return format(date, formatString);
  } catch (error) {
    return 'Invalid date';
  }
};

// Helper function to format medical history
const formatMedicalHistory = (medicalHistory) => {
  if (!medicalHistory) return 'No medical history available';

  const conditions = [];
  
  // Add boolean conditions
  if (medicalHistory.diabetic) conditions.push('Diabetic');
  if (medicalHistory.hypertension) conditions.push('Hypertension');
  if (medicalHistory.pregnant) conditions.push('Pregnant');
  
  // Add array items
  if (medicalHistory.allergies?.length > 0) {
    conditions.push(`Allergies: ${medicalHistory.allergies.join(', ')}`);
  }
  if (medicalHistory.currentMedications?.length > 0) {
    conditions.push(`Current Medications: ${medicalHistory.currentMedications.join(', ')}`);
  }
  if (medicalHistory.pastIllnesses?.length > 0) {
    conditions.push(`Past Illnesses: ${medicalHistory.pastIllnesses.join(', ')}`);
  }
  if (medicalHistory.surgeries?.length > 0) {
    conditions.push(`Surgeries: ${medicalHistory.surgeries.join(', ')}`);
  }

  return conditions.length > 0 ? conditions.join('\n') : 'No significant medical history';
};

const PatientDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [patient, setPatient] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [treatments, setTreatments] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [communications, setCommunications] = useState([]);
  const [prescriptions, setPrescriptions] = useState([]); // Added prescriptions state
  const [treatmentPlans, setTreatmentPlans] = useState([]); // Added treatment plans state
  const [selectedTreatmentPlan, setSelectedTreatmentPlan] = useState(null); // Selected plan for viewing
  const [viewPlanDialogOpen, setViewPlanDialogOpen] = useState(false); // Dialog for viewing plan
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [documentViewerOpen, setDocumentViewerOpen] = useState(false);
  const [currentDocument, setCurrentDocument] = useState(null);
  const [documentViewerUrl, setDocumentViewerUrl] = useState(null);
  const [documentViewerLoading, setDocumentViewerLoading] = useState(false);
  const [documentViewerFullscreen, setDocumentViewerFullscreen] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [newDocument, setNewDocument] = useState({
    file: null,
    category: '',
    description: ''
  });
  const [sendMessageDialogOpen, setSendMessageDialogOpen] = useState(false);
  const [newMessage, setNewMessage] = useState({
    type: 'WhatsApp',
    message: ''
  });
  const [invoiceDownloadDialog, setInvoiceDownloadDialog] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [invoiceDeleteDialogOpen, setInvoiceDeleteDialogOpen] = useState(false);
  const [invoiceDeleteLoading, setInvoiceDeleteLoading] = useState(false);
  const [pendingDeleteInvoiceId, setPendingDeleteInvoiceId] = useState(null);
  const [phoneCode, setPhoneCode] = useState('91');
  const [latestIntake, setLatestIntake] = useState(null);
  const [intakeLoading, setIntakeLoading] = useState(false);
  const [creatingIntakeLink, setCreatingIntakeLink] = useState(false);
  const [generatedIntakeUrl, setGeneratedIntakeUrl] = useState('');
  const [attachIntakeDialogOpen, setAttachIntakeDialogOpen] = useState(false);
  const [archiveSearch, setArchiveSearch] = useState('');
  const [archiveIntakes, setArchiveIntakes] = useState([]);
  const [archiveLoading, setArchiveLoading] = useState(false);
  const [selectedArchiveIntakeId, setSelectedArchiveIntakeId] = useState('');
  const [attachingArchiveIntake, setAttachingArchiveIntake] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const fetchPatientData = async () => {
      try {
        setLoading(true);
        setError(null);

        const { getOrganizationCurrency } = await import('../../utils/currencyUtils');
        const { getOrganizationPhoneSettings } = await import('../../utils/phoneUtils');
        await getOrganizationCurrency(true);
        const phoneSettings = await getOrganizationPhoneSettings(true);
        setPhoneCode(phoneSettings.phoneCode || '91');

        // Get token from localStorage
        const token = localStorage.getItem('token');
        
        // Make API calls to fetch patient data
        const patientResponse = await axios.get(`${API_URL}/patients/${id}`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        
        // Set patient data from API response
        setPatient(patientResponse.data.data);

        // Load latest submitted/reviewed intake (non-blocking for page rendering)
        setIntakeLoading(true);
        try {
          const intakeData = await intakeAPI.getLatestForPatient(id);
          setLatestIntake(intakeData || null);
        } catch (intakeErr) {
          setLatestIntake(null);
        } finally {
          setIntakeLoading(false);
        }
        
        // Fetch patient appointments
        const appointmentsResponse = await axios.get(`${API_URL}/appointments?patient=${id}`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        setAppointments(appointmentsResponse.data.data || []);
        
        // Fetch patient treatments
        const treatmentsResponse = await axios.get(`${API_URL}/treatments?patient=${id}`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        const treatmentsData = treatmentsResponse.data.data || [];
        setTreatments(treatmentsData);
        
        // Fetch patient invoices
        const invoicesResponse = await axios.get(`${API_URL}/billing?patient=${id}`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        setInvoices(invoicesResponse.data.data || []);
        
        // Fetch patient documents
        const documentsResponse = await axios.get(`${API_URL}/patients/${id}/documents`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        setDocuments(documentsResponse.data.data || []);
        
        // Fetch patient communications
        const communicationsResponse = await axios.get(`${API_URL}/patients/${id}/communications`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        setCommunications(communicationsResponse.data.data || []);

        // Fetch patient prescriptions
        const prescriptionsResponse = await axios.get(`${API_URL}/prescriptions/patient/${id}`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        
        // Handle different possible response structures
        let prescriptionsData = [];
        if (prescriptionsResponse.data) {
          if (prescriptionsResponse.data.data) {
            // If response has data.data structure (like getPrescriptionsByPatient)
            if (prescriptionsResponse.data.data.docs) {
              // Paginated response - prescriptions are in data.data.docs
              prescriptionsData = Array.isArray(prescriptionsResponse.data.data.docs) 
                ? prescriptionsResponse.data.data.docs 
                : [];
            } else {
              // Direct data array
              prescriptionsData = Array.isArray(prescriptionsResponse.data.data) 
                ? prescriptionsResponse.data.data 
                : [];
            }
          } else if (prescriptionsResponse.data.docs) {
            // If response has data.docs structure (pagination)
            prescriptionsData = Array.isArray(prescriptionsResponse.data.docs) 
              ? prescriptionsResponse.data.docs 
              : [];
          } else if (Array.isArray(prescriptionsResponse.data)) {
            // If response is directly an array
            prescriptionsData = prescriptionsResponse.data;
          }
        }
        
        setPrescriptions(prescriptionsData);

        // Fetch patient treatment plans
        const treatmentPlansResponse = await axios.get(`${API_URL}/treatment-plans`, {
          headers: {
            Authorization: `Bearer ${token}`
          },
          params: {
            patient: id
          }
        });
        // Filter treatment plans for this patient
        const patientPlans = (treatmentPlansResponse.data.data || []).filter(
          plan => (plan.patient?._id || plan.patient) === id
        );
        setTreatmentPlans(patientPlans);
        
      } catch (err) {
        console.error('Error fetching patient data:', err);
        setError(formatFailedActionMessage('load', 'patient details'));
      } finally {
        setLoading(false);
      }
    };

    fetchPatientData();
  }, [id]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      setLoading(true);
      
      // Get token from localStorage
      const token = localStorage.getItem('token');
      
      // Delete patient via API
      await axios.delete(`${API_URL}/patients/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      toast.success(formatSuccessActionMessage('deleted', 'Patient'));
      navigate('/patients');
    } catch (err) {
      console.error('Error deleting patient:', err);
      toast.error(formatFailedActionMessage('delete', 'patient'));
    } finally {
      setLoading(false);
      setDeleteDialogOpen(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  const handleGenerateIntakeLink = async () => {
    try {
      setCreatingIntakeLink(true);
      const result = await intakeAPI.createLink({ patientId: id });
      const fullUrl = `${window.location.origin}${result.publicUrl}`;
      setGeneratedIntakeUrl(fullUrl);
      await navigator.clipboard.writeText(fullUrl);
      toast.success('Intake link generated and copied');
    } catch (err) {
      toast.error(getApiErrorMessage(err, formatFailedActionMessage('generate', 'intake link')));
    } finally {
      setCreatingIntakeLink(false);
    }
  };

  const handleCopyIntakeLink = async () => {
    if (!generatedIntakeUrl) return;
    try {
      await navigator.clipboard.writeText(generatedIntakeUrl);
      toast.success('Link copied');
    } catch (err) {
      toast.error(formatFailedActionMessage('copy', 'link'));
    }
  };

  const handleOpenFullIntake = () => {
    if (!latestIntake?.intakeId) return;
    navigate(`/patients/${id}/intakes/${latestIntake.intakeId}`);
  };

  const handleMarkIntakeReviewed = async () => {
    if (!latestIntake?.token) return;
    try {
      await intakeAPI.markReviewed(latestIntake.token);
      toast.success('Intake marked as reviewed');
      const refreshed = await intakeAPI.getLatestForPatient(id);
      setLatestIntake(refreshed || null);
    } catch (err) {
      toast.error(getApiErrorMessage(err, formatFailedActionMessage('mark', 'intake as reviewed')));
    }
  };

  const loadUnattachedArchiveIntakes = async (searchValue = '') => {
    try {
      setArchiveLoading(true);
      const response = await intakeAPI.getArchive({
        attached: 'false',
        search: searchValue || undefined,
        limit: 50,
      });
      const list = (response?.data || []).filter((item) => item.status === 'submitted' || item.status === 'reviewed');
      setArchiveIntakes(list);
    } catch (err) {
      setArchiveIntakes([]);
    } finally {
      setArchiveLoading(false);
    }
  };

  const handleOpenAttachIntakeDialog = async () => {
    setAttachIntakeDialogOpen(true);
    setArchiveSearch('');
    setSelectedArchiveIntakeId('');
    await loadUnattachedArchiveIntakes('');
  };

  const handleAttachArchiveIntake = async () => {
    if (!selectedArchiveIntakeId) return;
    try {
      setAttachingArchiveIntake(true);
      await intakeAPI.attachToPatient(selectedArchiveIntakeId, id);
      toast.success('Intake attached to patient');
      setAttachIntakeDialogOpen(false);
      const refreshed = await intakeAPI.getLatestForPatient(id);
      setLatestIntake(refreshed || null);
    } catch (err) {
      toast.error(getApiErrorMessage(err, formatFailedActionMessage('attach', 'intake')));
    } finally {
      setAttachingArchiveIntake(false);
    }
  };

  // Invoice actions (same as Invoices page)
  const handleViewInvoice = (invoiceId) => {
    navigate(`/billing/invoice/${invoiceId}`);
  };
  const handleEditInvoice = (invoiceId) => {
    navigate(`/billing/invoice/${invoiceId}/edit`);
  };
  const handleRecordPayment = (invoiceId) => {
    navigate(`/billing/invoice/${invoiceId}/payment`);
  };
  const handlePrintInvoice = async (invoiceId) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Authentication token not found');
      const response = await axios.get(`${API_URL}/billing/${invoiceId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await openInvoicePDF(response.data.data);
    } catch (err) {
      console.error('Error printing invoice:', err);
      toast.error(formatFailedActionMessage('print', 'invoice'));
    }
  };
  const handleDownloadInvoice = async (invoiceId) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Authentication token not found');
      const response = await axios.get(`${API_URL}/billing/${invoiceId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedInvoice(response.data.data);
      setInvoiceDownloadDialog(true);
    } catch (err) {
      console.error('Error downloading invoice:', err);
      toast.error(formatFailedActionMessage('load', 'invoice'));
    }
  };
  const handleInvoiceDownloadFormat = async (format) => {
    try {
      if (format === 'pdf') {
        await downloadInvoicePDF(selectedInvoice);
      } else {
        downloadInvoiceCSV(selectedInvoice);
      }
      setInvoiceDownloadDialog(false);
      setSelectedInvoice(null);
    } catch (err) {
      toast.error(formatFailedActionMessage('download', 'invoice'));
    }
  };
  const requestDeleteInvoice = (invoiceId) => {
    setPendingDeleteInvoiceId(invoiceId);
    setInvoiceDeleteDialogOpen(true);
  };

  const handleDeleteInvoice = async () => {
    if (!pendingDeleteInvoiceId) return;
    setInvoiceDeleteLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Authentication token not found');
      await axios.delete(`${API_URL}/billing/${pendingDeleteInvoiceId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(formatSuccessActionMessage('deleted', 'Invoice'));
      setInvoices(prev => prev.filter(inv => inv._id !== pendingDeleteInvoiceId));
      setInvoiceDeleteDialogOpen(false);
      setPendingDeleteInvoiceId(null);
    } catch (err) {
      console.error('Error deleting invoice:', err);
      toast.error(formatFailedActionMessage('delete', 'invoice'));
    } finally {
      setInvoiceDeleteLoading(false);
    }
  };

  // Document management functions
  const handleUploadDialogOpen = () => {
    setUploadDialogOpen(true);
  };

  const handleUploadDialogClose = () => {
    setUploadDialogOpen(false);
    setNewDocument({
      file: null,
      category: '',
      description: ''
    });
    setUploadProgress(0);
    setUploadStatus(null);
  };

  const handleFileChange = (event) => {
    if (event.target.files && event.target.files[0]) {
      setNewDocument({
        ...newDocument,
        file: event.target.files[0]
      });
    }
  };

  const handleDocumentInputChange = (event) => {
    const { name, value } = event.target;
    setNewDocument({
      ...newDocument,
      [name]: value
    });
  };

  const handleDocumentUpload = async () => {
    // Validate inputs
    if (!newDocument.file) {
      showSnackbar('File is required for upload', 'error');
      return;
    }

    if (!newDocument.category) {
      showSnackbar('Document category is required', 'error');
      return;
    }

    try {
      // Get token from localStorage
      const token = localStorage.getItem('token');
      
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('file', newDocument.file);
      formData.append('category', newDocument.category);
      formData.append('description', newDocument.description || '');
      
      setUploadStatus('uploading');
      
      // Upload document via API
      const response = await axios.post(`${API_URL}/patients/${id}/documents`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
        }
      });
      
      setUploadStatus('success');
      
      // Add the new document to the documents list
      const newDoc = response.data.data;
      setDocuments([newDoc, ...documents]);
      
      setTimeout(() => {
        handleUploadDialogClose();
        showSnackbar(formatSuccessActionMessage('uploaded', 'Document'), 'success');
      }, 1000);
      
    } catch (err) {
      console.error('Error uploading document:', err);
      setUploadStatus('error');
      
      // Provide more specific error messages
      const errorMessage = getApiErrorMessage(err, formatFailedActionMessage('upload', 'document'));
      
      showSnackbar(errorMessage, 'error');
    }
  };

  const handleViewDocument = async (doc) => {
    setCurrentDocument(doc);
    setDocumentViewerOpen(true);
    setDocumentViewerUrl(null);
    setDocumentViewerLoading(true);
    const canPreview = doc.fileType?.startsWith('image/') || doc.fileType === 'application/pdf';
    if (canPreview) {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setDocumentViewerLoading(false);
          return;
        }
        const response = await axios.get(`${API_URL}/patients/${id}/documents/${doc._id}/download`, {
          responseType: 'blob',
          timeout: 30000
        });
        if (response.status === 200 && response.data?.size > 0) {
          const blob = new Blob([response.data], {
            type: response.headers['content-type'] || doc.fileType || 'application/octet-stream'
          });
          setDocumentViewerUrl(URL.createObjectURL(blob));
        }
      } catch (err) {
        console.error('Failed to load document for preview:', err);
        toast.error(formatFailedActionMessage('load', 'document for preview'));
      } finally {
        setDocumentViewerLoading(false);
      }
    } else {
      setDocumentViewerLoading(false);
    }
  };

  const handleCloseDocumentViewer = () => {
    if (documentViewerUrl) {
      URL.revokeObjectURL(documentViewerUrl);
    }
    setDocumentViewerOpen(false);
    setCurrentDocument(null);
    setDocumentViewerUrl(null);
    setDocumentViewerFullscreen(false);
  };

  const handleDeleteDocument = async (documentId) => {
    try {
      // Get token from localStorage
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication token not found');
      }
      
      // Make API call to delete document
      await axios.delete(`${API_URL}/patients/${id}/documents/${documentId}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      // Update local state
      setDocuments(documents.filter(doc => doc._id !== documentId));
      showSnackbar(formatSuccessActionMessage('deleted', 'Document'), 'success');
    } catch (err) {
      console.error('Error deleting document:', err);
      const errorMessage = getApiErrorMessage(err, formatFailedActionMessage('delete', 'document'));
      toast.error(errorMessage);
      showSnackbar(errorMessage, 'error');
    }
  };

  const handleDownloadDocument = async (doc) => {
    try {
      // Get token from localStorage
      const token = localStorage.getItem('token');
      if (!token) {
        showSnackbar('Log in again to download documents.', 'error');
        return;
      }
      
      // Check token validity
      if (!checkTokenValidity(token)) {
        showSnackbar('Session is invalid. Refreshing...', 'warning');
        forceTokenRefresh();
        return;
      }
      
      // Show downloading message
      
      // Make API call to download document using the global axios instance
      // This ensures we use the same configuration as other API calls
      const response = await axios.get(`${API_URL}/patients/${id}/documents/${doc._id}/download`, {
        responseType: 'blob',
        timeout: 30000
      });
      
      // Verify we got a successful response
      if (response.status !== 200) {
        throw new Error(`Download failed with status: ${response.status}`);
      }
      
      // Verify we got blob data
      if (!response.data || response.data.size === 0) {
        throw new Error('Download returned empty file');
      }
      
      // Create a blob URL and trigger download
      const blob = new Blob([response.data], { 
        type: response.headers['content-type'] || 'application/octet-stream' 
      });
      
      // Check if we're in a browser environment
      if (typeof window === 'undefined' || typeof window.document === 'undefined') {
        throw new Error('Not in a browser environment');
      }
      
      // Use a more reliable download method
      try {
        // Method 1: Create and click a link
        const url = window.URL.createObjectURL(blob);
        const link = window.document.createElement('a');
        link.href = url;
        link.download = doc.fileName;
        link.style.display = 'none';
        
        // Add to DOM, click, and cleanup
        window.document.body.appendChild(link);
        link.click();
        
        // Cleanup
        setTimeout(() => {
          try {
            if (window.document.body.contains(link)) {
              window.document.body.removeChild(link);
            }
          } catch (cleanupError) {
            console.warn('Cleanup error:', cleanupError);
          }
          window.URL.revokeObjectURL(url);
        }, 100);
        
      } catch (domError) {
        console.warn('DOM method failed, trying alternative download method:', domError);
        
        // Method 2: Use window.open as fallback
        try {
          const url = window.URL.createObjectURL(blob);
          window.open(url, '_blank');
          
          // Cleanup after a delay
          setTimeout(() => {
            window.URL.revokeObjectURL(url);
          }, 1000);
          
        } catch (fallbackError) {
          console.error('All download methods failed:', fallbackError);
          throw new Error('Unable to trigger download in this browser');
        }
      }
      
              showSnackbar(formatSuccessActionMessage('downloaded', doc.fileName), 'success');
      
    } catch (err) {
      console.error('Download error:', err);
      
      // Handle specific error cases
      if (err.response?.status === 401) {
        showSnackbar(SESSION_EXPIRED_MESSAGE, 'error');
        // Redirect to login after a short delay
        setTimeout(() => {
          window.location.href = '/login';
        }, 2000);
      } else if (err.response?.status === 404) {
        showSnackbar('Document not found on server', 'error');
      } else if (err.code === 'ECONNABORTED') {
        showSnackbar('Download timed out', 'error');
      } else {
        showSnackbar(formatFailedActionMessage('download', 'document'), 'error');
      }
    }
  };

  // Communication functions
  const handleSendMessageDialogOpen = () => {
    setSendMessageDialogOpen(true);
  };

  const handleSendMessageDialogClose = () => {
    setSendMessageDialogOpen(false);
    setNewMessage({
      type: 'WhatsApp',
      message: ''
    });
  };

  const handleMessageInputChange = (event) => {
    const { name, value } = event.target;
    setNewMessage({
      ...newMessage,
      [name]: value
    });
  };

  const handleSendMessage = async () => {
    // Validate inputs
    if (!newMessage.message.trim()) {
      showSnackbar('Message is required', 'error');
      return;
    }

    try {
      // Get token from localStorage
      const token = localStorage.getItem('token');
      
      // Send message via API
      const response = await axios.post(`${API_URL}/patients/${id}/communications`, newMessage, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      // Add the new communication to the list
      const newComm = response.data;
      
      // Update local state
      setCommunications([newComm, ...communications]);
      handleSendMessageDialogClose();
      showSnackbar(`Message sent via ${newMessage.type}`, 'success');
    } catch (err) {
      console.error('Error sending message:', err);
      showSnackbar(formatFailedActionMessage('send', 'message'), 'error');
    }
  };

  // Treatment Plan functions
  const handleViewTreatmentPlan = async (plan) => {
    setSelectedTreatmentPlan(plan);
    setViewPlanDialogOpen(true);
    // Ensure currency is loaded (force refresh to get latest currency)
    const { getOrganizationCurrency } = await import('../../utils/currencyUtils');
    await getOrganizationCurrency(true); // Force refresh to get latest currency
  };

  // Prescription functions
  const handleViewPrescription = (prescription) => {
    // For now, navigate to prescriptions page with the prescription ID
    // In the future, this could open a modal or navigate to a detailed view
    navigate(`/prescriptions?selectedPrescription=${prescription._id}`);
  };

  const handleEditPrescription = (prescription) => {
    // Navigate to prescriptions page with edit mode
    navigate(`/prescriptions?editPrescription=${prescription._id}`);
  };

  // Utility functions
  const showSnackbar = (message, severity = 'success') => {
    if (severity === 'error') toast.error(message);
    else if (severity === 'warning') toast.warning(message);
    else if (severity === 'info') toast.info(message);
    else toast.success(message);
  };

  const getFileIcon = (fileType) => {
    if (fileType.startsWith('image/')) {
      return <ImageIcon color="primary" />;
    } else if (fileType === 'application/pdf') {
      return <PdfIcon color="error" />;
    } else {
      return <FileIcon color="action" />;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
        <Box sx={{ mt: 2 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
          >
            Back to Patients
          </Button>
        </Box>
      </Container>
    );
  }

  if (!patient) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="warning">Patient not found</Alert>
        <Box sx={{ mt: 2 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
          >
            Back to Patients
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header with patient name and actions */}
      <Paper elevation={3} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 2 }}>
            {/* Left: Back button and Gender chip */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
              onClick={() => navigate(-1)}
              >
                Back
              </Button>
              <Chip 
                label={patient.gender === 'male' ? 'Male' : patient.gender === 'female' ? 'Female' : 'Other'} 
                color="primary" 
                size="small"
              />
            </Box>
            {/* Center: Patient Name - Large and Prominent */}
            <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', minWidth: { xs: '100%', sm: 'auto' }, order: { xs: 3, sm: 0 } }}>
              <Typography 
                variant="h3" 
                component="h1" 
                sx={{ 
                  fontWeight: 700,
                  fontSize: { xs: '1.75rem', sm: '2.25rem', md: '2.75rem' },
                  lineHeight: 1.2,
                  color: 'text.primary',
                  textAlign: 'center'
                }}
              >
                {patient?.name || 'Patient Name'}
              </Typography>
            </Box>
            {/* Right: Edit and Delete buttons */}
            <Box>
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={() => navigate(`/patients/${id}/edit`)}
                sx={{ mr: 1 }}
              >
                Edit
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={handleDeleteClick}
              >
                Delete
              </Button>
            </Box>
          </Box>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <PhoneIcon color="action" sx={{ mr: 1 }} />
              <Typography variant="body1">{formatPhoneForDisplay(patient.phone, phoneCode)}</Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <EmailIcon color="action" sx={{ mr: 1 }} />
              <Typography variant="body1">{patient.email || 'No email'}</Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="body2" color="textSecondary">
              Patient ID: {patient.patientId || patient._id}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="body2" color="textSecondary">
              Registered: {safeFormatDate(patient.createdAt)}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Tabs for different sections */}
      <Box sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
          <Tab label="Overview" />
          <Tab label="Appointments" />
          <Tab label="Plans" />
          <Tab label="Treatments" />
          <Tab label="Invoices" />
          <Tab label="Documents & Images" />
          <Tab label="Communication" />
          <Tab label="Prescriptions" />
        </Tabs>
      </Box>

      {/* Tab content */}
      <Box sx={{ mb: 4 }}>
        {/* Overview Tab */}
        {tabValue === 0 && (
          <Grid container spacing={3}>
            {/* Personal Information */}
            <Grid item xs={12} md={6}>
              <Paper elevation={2} sx={{ p: 3, height: '100%', borderRadius: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Personal Information
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="textSecondary">Full Name</Typography>
                    <Typography variant="body1">{patient.name}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="textSecondary">Date of Birth</Typography>
                    <Typography variant="body1">
                      {safeFormatDate(patient.dateOfBirth)} ({patient.age} years)
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="textSecondary">Gender</Typography>
                    <Typography variant="body1" sx={{ textTransform: 'capitalize' }}>
                      {patient.gender}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="textSecondary">Blood Group</Typography>
                    <Typography variant="body1">{patient.bloodGroup || 'Not specified'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="textSecondary">Occupation</Typography>
                    <Typography variant="body1">{patient.occupation || 'Not specified'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="textSecondary">Referred By</Typography>
                    <Typography variant="body1">{patient.referredBy || 'Not specified'}</Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            {/* Contact Information */}
            <Grid item xs={12} md={6}>
              <Paper elevation={2} sx={{ p: 3, height: '100%', borderRadius: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Contact Information
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary">Address</Typography>
                    <Typography variant="body1">
                      {patient.address?.street || ''}, {patient.address?.city || ''}, {patient.address?.state || ''} - {patient.address?.pincode || ''}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="textSecondary">Phone</Typography>
                    <Typography variant="body1">{formatPhoneForDisplay(patient.phone, phoneCode)}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="textSecondary">Email</Typography>
                    <Typography variant="body1">{patient.email || 'Not provided'}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary">Emergency Contact</Typography>
                    <Typography variant="body1">
                      {patient.emergencyContactName} (Phone: {formatPhoneForDisplay(patient.emergencyContactPhone, phoneCode)})
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            {/* Medical Information */}
            <Grid item xs={12}>
              <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Medical Information
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary">Medical History</Typography>
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                      {formatMedicalHistory(patient.medicalHistory)}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            {/* Intake Summary */}
            <Grid item xs={12}>
              <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                  <Typography variant="h6">Primary Intake (Medical + Dental + Surgical)</Typography>
                  <Button
                    variant="contained"
                    onClick={handleGenerateIntakeLink}
                    disabled={creatingIntakeLink}
                  >
                    {creatingIntakeLink ? 'Generating...' : 'Generate intake link'}
                  </Button>
                </Box>
                <Divider sx={{ my: 2 }} />

                {generatedIntakeUrl && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                      Public link
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                      <TextField
                        size="small"
                        fullWidth
                        value={generatedIntakeUrl}
                        InputProps={{ readOnly: true }}
                        sx={{
                          flex: 1,
                          minWidth: 0,
                          '& .MuiInputBase-input': {
                            wordBreak: 'break-all',
                            overflowWrap: 'anywhere',
                          },
                        }}
                      />
                      <Button
                        variant="outlined"
                        startIcon={<ContentCopyIcon />}
                        onClick={handleCopyIntakeLink}
                        sx={{ width: { xs: '100%', sm: 'auto' } }}
                      >
                        Copy
                      </Button>
                    </Box>
                  </Box>
                )}

                {intakeLoading ? (
                  <LinearProgress />
                ) : latestIntake ? (
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={3}>
                      <Typography variant="subtitle2" color="textSecondary">Risk Score</Typography>
                      <Typography variant="h5">{latestIntake.riskScore || 0}</Typography>
                    </Grid>
                    <Grid item xs={12} md={9}>
                      <Typography variant="subtitle2" color="textSecondary" gutterBottom>Risk Flags</Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {(latestIntake.riskFlags || []).length > 0 ? (
                          latestIntake.riskFlags.map((flag) => (
                            <Chip key={flag} label={flag} color="warning" size="small" />
                          ))
                        ) : (
                          <Chip label="No red flags" size="small" />
                        )}
                      </Box>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="textSecondary">Smoking</Typography>
                      <Typography variant="body2">
                        {latestIntake.answers?.med_53 || '—'} {latestIntake.answers?.cigarettesPerDay ? `(${latestIntake.answers.cigarettesPerDay}/day)` : ''}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="textSecondary">Diabetes</Typography>
                      <Typography variant="body2">
                        {latestIntake.answers?.med_23 || '—'} {latestIntake.answers?.med_23_hba1c ? `(HbA1c ${latestIntake.answers.med_23_hba1c})` : ''}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="textSecondary">Anticoagulants / Stroke</Typography>
                      <Typography variant="body2">{latestIntake.answers?.med_10 || '—'}</Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="textSecondary">Bisphosphonates</Typography>
                      <Typography variant="body2">{latestIntake.answers?.med_26_bisphosphonates || '—'}</Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="textSecondary">Radiation / Chemo</Typography>
                      <Typography variant="body2">
                        {latestIntake.answers?.med_41 || '—'} / {latestIntake.answers?.med_42 || '—'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="textSecondary">Allergy details</Typography>
                      <Typography variant="body2">{latestIntake.answers?.med_02_details || '—'}</Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="subtitle2" color="textSecondary">Extraction history</Typography>
                      <Typography variant="body2">{latestIntake.answers?.surg_01_extractionsHistory || '—'}</Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="subtitle2" color="textSecondary">Implant history</Typography>
                      <Typography variant="body2">{latestIntake.answers?.surg_02_implantsHistory || '—'}</Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="subtitle2" color="textSecondary">Healing</Typography>
                      <Typography variant="body2">{latestIntake.answers?.surg_07_healing || '—'}</Typography>
                    </Grid>

                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Button variant="outlined" onClick={handleOpenFullIntake}>
                          Open full intake
                        </Button>
                        <Button variant="outlined" onClick={handleOpenAttachIntakeDialog}>
                          Attach existing intake
                        </Button>
                        <Button
                          variant="outlined"
                          color="success"
                          onClick={handleMarkIntakeReviewed}
                          disabled={latestIntake.status === 'reviewed'}
                        >
                          {latestIntake.status === 'reviewed' ? 'Reviewed' : 'Mark as reviewed'}
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                ) : (
                  <Box>
                    <Alert severity="info" sx={{ mb: 1 }}>No submitted intake yet for this patient.</Alert>
                    <Button variant="outlined" onClick={handleOpenAttachIntakeDialog}>
                      Attach existing intake
                    </Button>
                  </Box>
                )}
              </Paper>
            </Grid>

            <Dialog open={attachIntakeDialogOpen} onClose={() => setAttachIntakeDialogOpen(false)} fullWidth maxWidth="md">
              <DialogTitle>Attach existing intake</DialogTitle>
              <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Choose a submitted/reviewed intake from archive and attach it to this patient.
                </Typography>
                <TextField
                  fullWidth
                  label="Search by name / phone / ID"
                  size="small"
                  value={archiveSearch}
                  onChange={(e) => setArchiveSearch(e.target.value)}
                  onKeyDown={async (e) => {
                    if (e.key === 'Enter') {
                      await loadUnattachedArchiveIntakes(archiveSearch);
                    }
                  }}
                  sx={{ mb: 2 }}
                />
                <Button variant="outlined" size="small" onClick={() => loadUnattachedArchiveIntakes(archiveSearch)} sx={{ mb: 2 }}>
                  Search
                </Button>
                {archiveLoading ? (
                  <LinearProgress />
                ) : (
                  <FormControl fullWidth size="small">
                    <InputLabel>Available intake</InputLabel>
                    <Select
                      label="Available intake"
                      value={selectedArchiveIntakeId}
                      onChange={(e) => setSelectedArchiveIntakeId(e.target.value)}
                    >
                      {archiveIntakes.map((item) => (
                        <MenuItem key={item.intakeId} value={item.intakeId}>
                          {item.answers?.fullName || 'Unnamed'} | ID: {item.answers?.patientRecordId || '—'} | {item.answers?.phone || '—'} | {item.submittedAt ? safeFormatDate(item.submittedAt, 'dd MMM yyyy HH:mm') : 'No date'}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setAttachIntakeDialogOpen(false)}>Cancel</Button>
                <Button
                  variant="contained"
                  onClick={handleAttachArchiveIntake}
                  disabled={!selectedArchiveIntakeId || attachingArchiveIntake}
                >
                  {attachingArchiveIntake ? 'Attaching...' : 'Attach'}
                </Button>
              </DialogActions>
            </Dialog>

            {/* Clinic Information */}
            <Grid item xs={12}>
              <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Clinic Information
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="textSecondary">Primary Clinic</Typography>
                    <Typography variant="body1">{patient.registeredClinic?.name || 'Not assigned'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="textSecondary">Registration Date</Typography>
                    <Typography variant="body1">
                      {safeFormatDate(patient.createdAt)}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        )}

        {/* Appointments Tab */}
        {tabValue === 1 && (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Appointments</Typography>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => navigate(`/appointments/add?patientId=${id}`)}
              >
                New Appointment
              </Button>
            </Box>

            {appointments.length === 0 ? (
              <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
                <Typography variant="body1" align="center">
                  No appointments found for this patient.
                </Typography>
              </Paper>
            ) : (
              <Grid container spacing={2}>
                {appointments.map((appointment) => (
                  <Grid item xs={12} key={appointment._id || appointment.id}>
                    <Card variant="outlined" sx={{ borderRadius: 2 }}>
                      <CardContent>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={3}>
                            <Typography variant="subtitle2" color="textSecondary">Date & Time</Typography>
                            <Typography variant="body1">
                              {safeFormatDate(appointment.appointmentDate, 'dd MMM yyyy HH:mm')}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={2}>
                            <Typography variant="subtitle2" color="textSecondary">Type</Typography>
                            <Typography variant="body1">{appointment.appointmentType}</Typography>
                          </Grid>
                          <Grid item xs={12} sm={2}>
                            <Typography variant="subtitle2" color="textSecondary">Dentist</Typography>
                            <Typography variant="body1">
                              {appointment.dentist?.firstName} {appointment.dentist?.lastName}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <Typography variant="subtitle2" color="textSecondary">Status</Typography>
                            <Chip
                              label={appointment.status}
                              color={
                                appointment.status === 'completed' ? 'success' :
                                appointment.status === 'scheduled' ? 'primary' :
                                appointment.status === 'cancelled' ? 'error' : 'default'
                              }
                              size="small"
                            />
                          </Grid>
                          <Grid item xs={12} sm={2} sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => navigate(`/appointments/${appointment._id || appointment.id}`)}
                            >
                              View
                            </Button>
                          </Grid>
                          {appointment.notes && (
                            <Grid item xs={12}>
                              <Typography variant="subtitle2" color="textSecondary">Notes</Typography>
                              <Typography variant="body2">{appointment.notes}</Typography>
                            </Grid>
                          )}
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        )}

        {/* Treatment Plans Tab */}
        {tabValue === 2 && (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box>
                <Typography variant="h6">Treatment Plans</Typography>
                <Typography variant="body2" color="text.secondary">
                  Showing treatment plans for {patient?.name || 'Patient'}
                  {Array.isArray(treatmentPlans) && treatmentPlans.length > 0 && (
                    <span> • {treatmentPlans.length} plan(s) found</span>
                  )}
                </Typography>
              </Box>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => navigate(`/treatments/plans?patientId=${patient._id}`)}
              >
                Create Plan
              </Button>
            </Box>

            {(Array.isArray(treatmentPlans) && treatmentPlans.length === 0) ? (
              <Paper elevation={2} sx={{ p: 3, textAlign: 'center', borderRadius: 2 }}>
                <MedicalIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No Treatment Plans Found
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  This patient doesn't have any treatment plans yet. Create their first plan to get started.
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => navigate(`/treatments/plans?patientId=${patient._id}`)}
                >
                  Create First Plan
                </Button>
              </Paper>
            ) : (
              <Grid container spacing={2}>
                {treatmentPlans.map((plan) => (
                  <Grid item xs={12} md={6} lg={4} key={plan._id || plan.id}>
                    <Card elevation={2} sx={{ borderRadius: 2, height: '100%' }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                          <Typography variant="h6" component="h3">
                            {plan.name}
                          </Typography>
                          <Chip
                            label={plan.status || 'draft'}
                            color={
                              plan.status === 'active' ? 'success' :
                              plan.status === 'completed' ? 'primary' :
                              plan.status === 'cancelled' ? 'error' : 'default'
                            }
                            size="small"
                          />
                        </Box>
                        
                        {plan.description && (
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            {plan.description}
                          </Typography>
                        )}

                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            <MedicalIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                            {Array.isArray(plan.treatments) ? plan.treatments.length : 0} treatment(s)
                          </Typography>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            <CalendarIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                            {plan.estimatedDuration || 0} mins
                          </Typography>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            <MoneyIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                            {formatCurrencySync(plan.totalCost || 0)}
                          </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<ViewIcon />}
                            onClick={() => handleViewTreatmentPlan(plan)}
                          >
                            View
                          </Button>
                          <Tooltip title="Print Plan">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={async () => {
                                try {
                                  const token = localStorage.getItem('token');
                                  if (!token) {
                                    toast.error(SESSION_EXPIRED_MESSAGE);
                                    return;
                                  }
                                  const response = await axios.get(`${API_URL}/treatment-plans/${plan._id || plan.id}`, {
                                    headers: { Authorization: `Bearer ${token}` }
                                  });
                                  await openTreatmentPlanPDF(response.data.data);
                                } catch (error) {
                                  console.error('Error printing treatment plan:', error);
                                  toast.error(formatFailedActionMessage('print', 'treatment plan'));
                                }
                              }}
                            >
                              <PrintIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Download Plan">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={async () => {
                                try {
                                  const token = localStorage.getItem('token');
                                  if (!token) {
                                    toast.error(SESSION_EXPIRED_MESSAGE);
                                    return;
                                  }
                                  const response = await axios.get(`${API_URL}/treatment-plans/${plan._id || plan.id}`, {
                                    headers: { Authorization: `Bearer ${token}` }
                                  });
                                  await downloadTreatmentPlanPDF(response.data.data);
                                  toast.success(formatSuccessActionMessage('downloaded', 'Treatment plan'));
                                } catch (error) {
                                  console.error('Error downloading treatment plan:', error);
                                  toast.error(formatFailedActionMessage('download', 'treatment plan'));
                                }
                              }}
                            >
                              <DownloadIcon />
                            </IconButton>
                          </Tooltip>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<EditIcon />}
                            onClick={() => navigate(`/treatments/plans?edit=${plan._id || plan.id}&patientId=${patient._id}`)}
                          >
                            Edit
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        )}

        {/* Treatments Tab */}
        {tabValue === 3 && (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Treatments</Typography>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => navigate(`/treatments/add?patientId=${id}`)}
              >
                New Treatment
              </Button>
            </Box>

            {treatments.length === 0 ? (
              <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
                <Typography variant="body1" align="center">
                  No treatments found for this patient.
                </Typography>
              </Paper>
            ) : (
              <Grid container spacing={2}>
                {treatments.map((treatment) => (
                  <Grid item xs={12} key={treatment._id || treatment.id}>
                    <Card variant="outlined" sx={{ borderRadius: 2 }}>
                      <CardContent>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={2}>
                            <Typography variant="subtitle2" color="textSecondary">Treatment</Typography>
                            <Typography variant="body1">{treatment.name}</Typography>
                          </Grid>
                          <Grid item xs={12} sm={1.5}>
                            <Typography variant="subtitle2" color="textSecondary">Category</Typography>
                            <Typography variant="body1">{treatment.category}</Typography>
                          </Grid>
                          <Grid item xs={12} sm={2}>
                            <Typography variant="subtitle2" color="textSecondary">Teeth</Typography>
                            <Typography variant="body1">
                              {treatment.teeth && Array.isArray(treatment.teeth) && treatment.teeth.length > 0
                                ? treatment.teeth.sort((a, b) => a - b).join(', ')
                                : 'N/A'}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={2}>
                            <Typography variant="subtitle2" color="textSecondary">Date & Time</Typography>
                            <Typography variant="body1">
                              {treatment.treatmentDate 
                                ? safeFormatDate(treatment.treatmentDate, 'dd MMM yyyy')
                                : 'Not specified'}
                            </Typography>
                            {treatment.treatmentTime && (
                              <Typography variant="body2" color="text.secondary">
                                {treatment.treatmentTime}
                              </Typography>
                            )}
                          </Grid>
                          <Grid item xs={12} sm={1.5}>
                            <Typography variant="subtitle2" color="textSecondary">Duration</Typography>
                            <Typography variant="body1">{treatment.duration} minutes</Typography>
                          </Grid>
                          <Grid item xs={12} sm={1.5}>
                            <Typography variant="subtitle2" color="textSecondary">Price</Typography>
                            <Typography variant="body1">{formatCurrencySync(treatment.price || 0)}</Typography>
                          </Grid>
                          <Grid item xs={12} sm={1.5} sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 0.5 }}>
                            <Tooltip title="Print Treatment">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={async () => {
                                  try {
                                    const token = localStorage.getItem('token');
                                    if (!token) {
                                      toast.error(SESSION_EXPIRED_MESSAGE);
                                      return;
                                    }
                                    const response = await axios.get(`${API_URL}/treatments/${treatment._id || treatment.id}`, {
                                      headers: { Authorization: `Bearer ${token}` }
                                    });
                                    await openTreatmentPDF(response.data.data);
                                  } catch (error) {
                                    console.error('Error printing treatment:', error);
                                    toast.error(formatFailedActionMessage('print', 'treatment'));
                                  }
                                }}
                              >
                                <PrintIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Download PDF">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={async () => {
                                  try {
                                    const token = localStorage.getItem('token');
                                    if (!token) {
                                      toast.error(SESSION_EXPIRED_MESSAGE);
                                      return;
                                    }
                                    const response = await axios.get(`${API_URL}/treatments/${treatment._id || treatment.id}`, {
                                      headers: { Authorization: `Bearer ${token}` }
                                    });
                                    await downloadTreatmentPDF(response.data.data);
                                    toast.success(formatSuccessActionMessage('downloaded', 'Treatment PDF'));
                                  } catch (error) {
                                    console.error('Error downloading treatment:', error);
                                    toast.error(formatFailedActionMessage('download', 'treatment'));
                                  }
                                }}
                              >
                                <DownloadIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => navigate(`/treatments/${treatment._id || treatment.id}`)}
                            >
                              View
                            </Button>
                          </Grid>
                          {treatment.notes && (
                            <Grid item xs={12}>
                              <Typography variant="subtitle2" color="textSecondary">Notes</Typography>
                              <Typography variant="body2">{treatment.notes}</Typography>
                            </Grid>
                          )}
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        )}

        {/* Invoices Tab */}
        {tabValue === 4 && (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Invoices</Typography>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => navigate(`/billing/create?patientId=${id}`)}
              >
                New Invoice
              </Button>
            </Box>

            {invoices.length === 0 ? (
              <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
                <Typography variant="body1" align="center">
                  No invoices found for this patient.
                </Typography>
              </Paper>
            ) : (
              <Grid container spacing={2}>
                {invoices.map((invoice) => (
                  <Grid item xs={12} key={invoice._id}>
                    <Card variant="outlined" sx={{ borderRadius: 2 }}>
                      <CardContent>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={2}>
                            <Typography variant="subtitle2" color="textSecondary">Invoice #</Typography>
                            <Typography variant="body1">{invoice.invoiceNumber || '-'}</Typography>
                          </Grid>
                          <Grid item xs={12} sm={2}>
                            <Typography variant="subtitle2" color="textSecondary">Date</Typography>
                            <Typography variant="body1">{safeFormatDate(invoice.invoiceDate)}</Typography>
                          </Grid>
                          <Grid item xs={12} sm={2}>
                            <Typography variant="subtitle2" color="textSecondary">Amount</Typography>
                            <Typography variant="body1">{formatCurrencySync(invoice.totalAmount || 0)}</Typography>
                          </Grid>
                          <Grid item xs={12} sm={2}>
                            <Typography variant="subtitle2" color="textSecondary">Status</Typography>
                            <Chip
                              label={(invoice.paymentStatus || 'unpaid').charAt(0).toUpperCase() + (invoice.paymentStatus || 'unpaid').slice(1)}
                              color={
                                invoice.paymentStatus === 'paid' ? 'success' :
                                invoice.paymentStatus === 'partially paid' ? 'warning' :
                                invoice.paymentStatus === 'overdue' ? 'error' : 'default'
                              }
                              size="small"
                            />
                          </Grid>
                          <Grid item xs={12} sm={4} sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 0.5 }}>
                            <Tooltip title="View">
                              <IconButton size="small" color="primary" onClick={() => handleViewInvoice(invoice._id)}>
                                <ViewIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Edit">
                              <IconButton size="small" color="primary" onClick={() => handleEditInvoice(invoice._id)}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Print">
                              <IconButton size="small" color="primary" onClick={() => handlePrintInvoice(invoice._id)}>
                                <PrintIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Download">
                              <IconButton size="small" color="primary" onClick={() => handleDownloadInvoice(invoice._id)}>
                                <FileDownloadIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {invoice.paymentStatus !== 'paid' && (
                              <Tooltip title="Record Payment">
                                <IconButton size="small" color="success" onClick={() => handleRecordPayment(invoice._id)}>
                                  <PaymentIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip title="Delete">
                              <IconButton size="small" color="error" onClick={() => requestDeleteInvoice(invoice._id)}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Grid>
                          <Grid item xs={12}>
                            <Typography variant="subtitle2" color="textSecondary">Items</Typography>
                            <List dense>
                              {(invoice.items || []).map((item, index) => (
                                <ListItem key={index} disableGutters>
                                  <ListItemText
                                    primary={item.description || item.name || `Item ${index + 1}`}
                                    secondary={formatCurrencySync(item.amount || item.unitPrice || 0)}
                                  />
                                </ListItem>
                              ))}
                            </List>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        )}

        {/* Documents & Images Tab */}
        {tabValue === 5 && (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Documents & Images</Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={forceTokenRefresh}
                  sx={{ fontSize: '0.75rem' }}
                >
                  Refresh Session
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<CloudUploadIcon />}
                  onClick={handleUploadDialogOpen}
                >
                  Upload Document
                </Button>
              </Box>
            </Box>

            {documents.length === 0 ? (
              <Paper elevation={2} sx={{ p: 3, textAlign: 'center', borderRadius: 2 }}>
                <DocumentIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No Documents Yet
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Upload patient documents such as X-rays, lab reports, prescriptions, and consent forms.
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<CloudUploadIcon />}
                  onClick={handleUploadDialogOpen}
                >
                  Upload First Document
                </Button>
              </Paper>
            ) : (
              <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2 }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>File</TableCell>
                      <TableCell>Category</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell>Upload Date</TableCell>
                      <TableCell>Size</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {documents.map((document) => (
                      <TableRow key={document._id}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {getFileIcon(document.fileType)}
                            <Typography variant="body2" sx={{ ml: 1 }}>
                              {document.fileName}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip label={document.category} size="small" />
                        </TableCell>
                        <TableCell>{document.description || '-'}</TableCell>
                        <TableCell>{safeFormatDate(document.uploadedAt)}</TableCell>
                        <TableCell>{document.fileSize ? `${(document.fileSize / 1024).toFixed(1)} KB` : '-'}</TableCell>
                        <TableCell align="right">
                          <Tooltip title="View">
                            <IconButton onClick={() => handleViewDocument(document)} size="small">
                              <ViewIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Download">
                            <IconButton onClick={() => handleDownloadDocument(document)} size="small">
                              <DownloadIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton 
                              onClick={() => handleDeleteDocument(document._id)} 
                              size="small" 
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}

        {/* Communication Tab */}
        {tabValue === 6 && (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Communication History</Typography>
              <Button
                variant="contained"
                color="primary"
                startIcon={<SendIcon />}
                onClick={handleSendMessageDialogOpen}
              >
                Send Message
              </Button>
            </Box>

            {communications.length === 0 ? (
              <Paper elevation={2} sx={{ p: 3, textAlign: 'center', borderRadius: 2 }}>
                <MessageIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No Communication History
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Send appointment reminders, treatment follow-ups, or general communications to the patient.
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<SendIcon />}
                  onClick={handleSendMessageDialogOpen}
                >
                  Send First Message
                </Button>
              </Paper>
            ) : (
              <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2 }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date & Time</TableCell>
                      <TableCell>Channel</TableCell>
                      <TableCell>Message</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Response</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {communications.map((comm) => (
                      <TableRow key={comm.id}>
                        <TableCell>
                          {safeFormatDate(comm.date)}, {comm.time}
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={comm.type} 
                            size="small" 
                            color={comm.type === 'WhatsApp' ? 'success' : 'primary'} 
                          />
                        </TableCell>
                        <TableCell>{comm.message}</TableCell>
                        <TableCell>
                          <Chip 
                            label={comm.status} 
                            size="small" 
                            color={comm.status === 'Delivered' || comm.status === 'Read' ? 'success' : 'default'} 
                          />
                        </TableCell>
                        <TableCell>
                          {comm.response ? (
                            <Chip 
                              label={comm.response} 
                              size="small" 
                              color="info" 
                            />
                          ) : (
                            <Typography variant="body2" color="text.secondary">No response</Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}

        {/* Prescriptions Tab */}
        {tabValue === 7 && (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box>
                <Typography variant="h6">Prescription History</Typography>
                <Typography variant="body2" color="text.secondary">
                  Showing prescriptions for {patient?.name || 'Patient'}
                  {Array.isArray(prescriptions) && prescriptions.length > 0 && (
                    <span> • {prescriptions.length} prescription(s) found</span>
                  )}
                </Typography>
              </Box>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => navigate(`/prescriptions?patientId=${patient._id}`)}
              >
                Create Prescription
              </Button>
            </Box>

            {(Array.isArray(prescriptions) && prescriptions.length === 0) ? (
              <Paper elevation={2} sx={{ p: 3, textAlign: 'center', borderRadius: 2 }}>
                <PharmacyIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No Prescriptions Found
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  This patient hasn't been prescribed any medications yet. Create their first prescription to get started.
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => navigate(`/prescriptions?patientId=${patient._id}`)}
                >
                  Create First Prescription
                </Button>
              </Paper>
            ) : (
              <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2 }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Prescription #</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Doctor</TableCell>
                      <TableCell>Diagnosis</TableCell>
                      <TableCell>Medications</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(Array.isArray(prescriptions) ? prescriptions : []).map((prescription) => (
                      <TableRow key={prescription._id}>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {prescription.prescriptionNumber}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {safeFormatDate(prescription.date)}
                        </TableCell>
                        <TableCell>
                          Dr. {prescription.doctor?.firstName || ''} {prescription.doctor?.lastName || 'Unknown'}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                            {prescription.diagnosis || 'No diagnosis'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                            {prescription.medications?.length || 0} medication(s)
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={prescription.status.charAt(0).toUpperCase() + prescription.status.slice(1)}
                            color={
                              prescription.status === 'active' ? 'primary' :
                              prescription.status === 'completed' ? 'success' :
                              prescription.status === 'cancelled' ? 'error' : 'default'
                            }
                            size="small"
                          />
                          {prescription.isIssuedToPatient && (
                            <Chip
                              label="Issued"
                              color="success"
                              size="small"
                              sx={{ ml: 0.5 }}
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Tooltip title="View Details">
                              <IconButton
                                size="small"
                                onClick={() => handleViewPrescription(prescription)}
                              >
                                <ViewIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Edit Prescription">
                              <IconButton
                                size="small"
                                color="primary"
                                disabled={prescription.isIssuedToPatient}
                                onClick={() => handleEditPrescription(prescription)}
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}
      </Box>

      {/* View Treatment Plan Dialog */}
      <Dialog
        open={viewPlanDialogOpen}
        onClose={() => setViewPlanDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        {selectedTreatmentPlan && (
          <>
            <DialogTitle>
              Treatment Plan Details
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={3} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                  <Typography variant="h6">{selectedTreatmentPlan.name}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedTreatmentPlan.description || 'No description'}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle1" gutterBottom>
                    Patient: {patient?.name || 'Patient'}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle1" gutterBottom>
                    Treatments
                  </Typography>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Treatment</TableCell>
                          <TableCell>Category</TableCell>
                          <TableCell>Duration</TableCell>
                          <TableCell>Unit Price</TableCell>
                          <TableCell>Quantity</TableCell>
                          <TableCell>Total Price</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(selectedTreatmentPlan.treatments || []).map((treatmentEntry, index) => {
                          const treatment = treatmentEntry.treatment || treatmentEntry;
                          const teeth = treatmentEntry.teeth || [];
                          const quantity = treatmentEntry.quantity || 1;
                          const totalCost = treatmentEntry.cost || treatment.price || 0;
                          // Calculate unit price
                          const unitPrice = quantity > 0 ? totalCost / quantity : totalCost;
                          const treatmentId = treatment._id || treatmentEntry._id || index;
                          // Get treatment name - handle both populated object and ID
                          const treatmentName = treatment?.name || treatment?.treatmentName || 'Unknown Treatment';
                          const treatmentCategory = treatment?.category || 'N/A';
                          const treatmentDuration = treatmentEntry.duration || treatment?.duration || 0;
                          
                          return (
                            <TableRow key={treatmentId}>
                              <TableCell>
                                <Typography variant="body2" fontWeight="medium">
                                  {treatmentName}
                                </Typography>
                                {teeth.length > 0 && (
                                  <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                                    Teeth: {teeth.join(', ')}
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell>{treatmentCategory}</TableCell>
                              <TableCell>{treatmentDuration} mins</TableCell>
                              <TableCell>{formatCurrencySync(unitPrice)}</TableCell>
                              <TableCell>
                                {quantity > 1 ? (
                                  <Chip label={`×${quantity}`} size="small" color="primary" />
                                ) : (
                                  '1'
                                )}
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" fontWeight="medium">
                                  {formatCurrencySync(totalCost)}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <Typography variant="h6" component="span">
                      Total:
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                      <Typography variant="h6" component="span" color="primary">
                        {formatCurrencySync(selectedTreatmentPlan.totalCost || 0)}
                      </Typography>
                      <Typography variant="body1" component="span" color="text.secondary">
                        •
                      </Typography>
                      <Typography variant="h6" component="span">
                        {selectedTreatmentPlan.estimatedDuration || 0} mins
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button
                startIcon={<PdfIcon />}
                onClick={async () => {
                  try {
                    const token = localStorage.getItem('token');
                    if (!token) {
                      toast.error(SESSION_EXPIRED_MESSAGE);
                      return;
                    }
                    const response = await axios.get(`${API_URL}/treatment-plans/${selectedTreatmentPlan._id || selectedTreatmentPlan.id}`, {
                      headers: { Authorization: `Bearer ${token}` }
                    });
                    await openTreatmentPlanPDF(response.data.data);
                  } catch (error) {
                    console.error('Error previewing treatment plan:', error);
                    toast.error(formatFailedActionMessage('preview', 'treatment plan'));
                  }
                }}
              >
                Preview
              </Button>
              <Button
                startIcon={<PrintIcon />}
                onClick={async () => {
                  try {
                    const token = localStorage.getItem('token');
                    if (!token) {
                      toast.error(SESSION_EXPIRED_MESSAGE);
                      return;
                    }
                    const response = await axios.get(`${API_URL}/treatment-plans/${selectedTreatmentPlan._id || selectedTreatmentPlan.id}`, {
                      headers: { Authorization: `Bearer ${token}` }
                    });
                    await openTreatmentPlanPDF(response.data.data);
                  } catch (error) {
                    console.error('Error printing treatment plan:', error);
                    toast.error(formatFailedActionMessage('print', 'treatment plan'));
                  }
                }}
              >
                Print
              </Button>
              <Button
                startIcon={<DownloadIcon />}
                onClick={async () => {
                  try {
                    const token = localStorage.getItem('token');
                    if (!token) {
                      toast.error(SESSION_EXPIRED_MESSAGE);
                      return;
                    }
                    const response = await axios.get(`${API_URL}/treatment-plans/${selectedTreatmentPlan._id || selectedTreatmentPlan.id}`, {
                      headers: { Authorization: `Bearer ${token}` }
                    });
                    await downloadTreatmentPlanPDF(response.data.data);
                    toast.success(formatSuccessActionMessage('downloaded', 'Treatment plan'));
                  } catch (error) {
                    console.error('Error downloading treatment plan:', error);
                    toast.error(formatFailedActionMessage('download', 'treatment plan'));
                  }
                }}
              >
                Download
              </Button>
              <Button onClick={() => setViewPlanDialogOpen(false)}>
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
      >
        <DialogTitle>Delete Patient</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete {patient.name}? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            {loading ? <CircularProgress size={24} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Invoice Download Format Dialog */}
      <Dialog open={invoiceDownloadDialog} onClose={() => { setInvoiceDownloadDialog(false); setSelectedInvoice(null); }} maxWidth="sm" fullWidth>
        <DialogTitle>Choose Download Format</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Select the format you would like to download the invoice:
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Button
                fullWidth
                variant="outlined"
                size="large"
                onClick={() => handleInvoiceDownloadFormat('pdf')}
                sx={{ height: 80, flexDirection: 'column', border: '2px solid #1976d2', '&:hover': { border: '2px solid #1565c0' } }}
              >
                <Typography variant="h6" color="primary">PDF</Typography>
                <Typography variant="body2" color="text.secondary">Professional format</Typography>
              </Button>
            </Grid>
            <Grid item xs={6}>
              <Button
                fullWidth
                variant="outlined"
                size="large"
                onClick={() => handleInvoiceDownloadFormat('csv')}
                sx={{ height: 80, flexDirection: 'column', border: '2px solid #2e7d32', '&:hover': { border: '2px solid #1b5e20' } }}
              >
                <Typography variant="h6" color="success.main">CSV</Typography>
                <Typography variant="body2" color="text.secondary">Data analysis</Typography>
              </Button>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setInvoiceDownloadDialog(false); setSelectedInvoice(null); }}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Document Upload Dialog */}
      <Dialog open={uploadDialogOpen} onClose={handleUploadDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          Upload Document
          <IconButton
            aria-label="close"
            onClick={handleUploadDialogClose}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            {uploadStatus === 'uploading' ? (
              <Box sx={{ width: '100%', mt: 2, mb: 2 }}>
                <Typography variant="body2" gutterBottom>
                  Uploading: {uploadProgress}%
                </Typography>
                <LinearProgress variant="determinate" value={uploadProgress} />
              </Box>
            ) : uploadStatus === 'success' ? (
              <Alert severity="success" sx={{ mt: 2, mb: 2 }}>
                {formatSuccessActionMessage('uploaded', 'Document')}
              </Alert>
            ) : uploadStatus === 'error' ? (
              <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
                {formatFailedActionMessage('upload', 'document')}
              </Alert>
            ) : (
              <>
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.dicom"
                  style={{ display: 'none' }}
                  ref={fileInputRef}
                  onChange={handleFileChange}
                />
                <Box 
                  sx={{
                    border: '2px dashed #ccc',
                    borderRadius: 2,
                    p: 3,
                    textAlign: 'center',
                    mb: 3,
                    cursor: 'pointer',
                    '&:hover': { borderColor: 'primary.main' },
                  }}
                  onClick={() => fileInputRef.current.click()}
                >
                  <CloudUploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                  <Typography variant="body1" gutterBottom>
                    {newDocument.file ? newDocument.file.name : 'Click to select or drag and drop file here'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Supported formats: PDF, JPG, JPEG, PNG, DICOM
                  </Typography>
                </Box>

                <FormControl fullWidth margin="normal" required>
                  <InputLabel>Document Category</InputLabel>
                  <Select
                    name="category"
                    value={newDocument.category}
                    onChange={handleDocumentInputChange}
                    label="Document Category"
                  >
                    <MenuItem value="X-Ray (RVG/OPG)">X-Ray (RVG/OPG)</MenuItem>
                    <MenuItem value="Lab Report">Lab Report</MenuItem>
                    <MenuItem value="Prescription">Prescription</MenuItem>
                    <MenuItem value="Medical History Record">Medical History Record</MenuItem>
                    <MenuItem value="Consent Form">Consent Form</MenuItem>
                    <MenuItem value="Insurance Document">Insurance Document</MenuItem>
                    <MenuItem value="Treatment Plan">Treatment Plan</MenuItem>
                    <MenuItem value="Other">Other</MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  margin="normal"
                  fullWidth
                  name="description"
                  label="Description (Optional)"
                  value={newDocument.description}
                  onChange={handleDocumentInputChange}
                  multiline
                  rows={2}
                />
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleUploadDialogClose}>Cancel</Button>
          {uploadStatus !== 'success' && (
            <Button 
              onClick={handleDocumentUpload} 
              variant="contained" 
              disabled={uploadStatus === 'uploading' || !newDocument.file}
            >
              Upload
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Document Viewer Dialog */}
      <Dialog open={documentViewerOpen} onClose={handleCloseDocumentViewer} maxWidth="lg" fullWidth fullScreen={documentViewerFullscreen}>
        {currentDocument && (
          <>
            <DialogTitle>
              {currentDocument.fileName}
              <Tooltip title={documentViewerFullscreen ? 'Exit fullscreen' : 'Fullscreen'}>
                <IconButton
                  aria-label={documentViewerFullscreen ? 'exit fullscreen' : 'fullscreen'}
                  onClick={() => setDocumentViewerFullscreen(!documentViewerFullscreen)}
                  sx={{ position: 'absolute', right: 48, top: 8 }}
                >
                  {documentViewerFullscreen ? <ExitFullscreenIcon /> : <FullscreenIcon />}
                </IconButton>
              </Tooltip>
              <IconButton
                aria-label="close"
                onClick={handleCloseDocumentViewer}
                sx={{ position: 'absolute', right: 8, top: 8 }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Category: {currentDocument.category}
                </Typography>
                <Typography variant="body2" gutterBottom>
                  {currentDocument.description}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Uploaded on {safeFormatDate(currentDocument.uploadedAt)} by {currentDocument.uploadedBy?.name || 'Unknown User'}
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Box
                sx={{
                  height: documentViewerFullscreen ? 'calc(100vh - 180px)' : '60vh',
                  minHeight: 300,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  resize: documentViewerUrl ? 'both' : 'none',
                  overflow: 'auto',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                }}
              >
                {currentDocument.fileType.startsWith('image/') ? (
                  documentViewerLoading ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                      <CircularProgress />
                      <Typography variant="body2">Loading image...</Typography>
                    </Box>
                  ) : documentViewerUrl ? (
                    <Box sx={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', bgcolor: '#f5f5f5', p: 2 }}>
                      <img
                        src={documentViewerUrl}
                        alt={currentDocument.fileName}
                        style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                      />
                    </Box>
                  ) : (
                    <Box sx={{ width: '100%', height: '100%', bgcolor: '#f5f5f5', borderRadius: 1, p: 2, textAlign: 'center' }}>
                      <ImageIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
                      <Typography variant="body1">Could not load image.</Typography>
                      <Button variant="outlined" startIcon={<DownloadIcon />} sx={{ mt: 2 }} onClick={() => handleDownloadDocument(currentDocument)}>
                        Download Image
                      </Button>
                    </Box>
                  )
                ) : currentDocument.fileType === 'application/pdf' ? (
                  documentViewerLoading ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                      <CircularProgress />
                      <Typography variant="body2">Loading PDF...</Typography>
                    </Box>
                  ) : documentViewerUrl ? (
                    <Box sx={{ width: '100%', height: '100%', minHeight: 400, bgcolor: '#525659', overflow: 'hidden' }}>
                      <iframe
                        src={documentViewerUrl}
                        title={currentDocument.fileName}
                        style={{ width: '100%', height: '100%', minHeight: 400, border: 'none' }}
                      />
                    </Box>
                  ) : (
                    <Box sx={{ width: '100%', height: '100%', bgcolor: '#f5f5f5', borderRadius: 1, p: 2, textAlign: 'center' }}>
                      <PdfIcon sx={{ fontSize: 60, color: 'error.main', mb: 2 }} />
                      <Typography variant="body1">Could not load PDF.</Typography>
                      <Button variant="outlined" startIcon={<DownloadIcon />} sx={{ mt: 2 }} onClick={() => handleDownloadDocument(currentDocument)}>
                        Download PDF
                      </Button>
                    </Box>
                  )
                ) : (
                  <Box sx={{ width: '100%', height: '100%', bgcolor: '#f5f5f5', borderRadius: 1, p: 2, textAlign: 'center' }}>
                    <FileIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
                    <Typography variant="body1">
                      This file type cannot be previewed.
                    </Typography>
                    <Button 
                      variant="outlined" 
                      startIcon={<DownloadIcon />} 
                      sx={{ mt: 2 }}
                      onClick={() => handleDownloadDocument(currentDocument)}
                    >
                      Download File
                    </Button>
                  </Box>
                )}
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDocumentViewer}>Close</Button>
              <Button 
                startIcon={<DownloadIcon />} 
                onClick={() => handleDownloadDocument(currentDocument)}
              >
                Download
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Send Message Dialog */}
      <Dialog open={sendMessageDialogOpen} onClose={handleSendMessageDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          Send Message to Patient
          <IconButton
            aria-label="close"
            onClick={handleSendMessageDialogClose}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <FormControl fullWidth margin="normal">
              <InputLabel>Communication Channel</InputLabel>
              <Select
                name="type"
                value={newMessage.type}
                onChange={handleMessageInputChange}
                label="Communication Channel"
              >
                <MenuItem value="WhatsApp">WhatsApp</MenuItem>
                <MenuItem value="SMS">SMS</MenuItem>
              </Select>
            </FormControl>

            <TextField
              margin="normal"
              fullWidth
              name="message"
              label="Message"
              value={newMessage.message}
              onChange={handleMessageInputChange}
              multiline
              rows={4}
              required
            />

            <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                Patient Contact Information:
              </Typography>
              <Typography variant="body2">
                Phone: {formatPhoneForDisplay(patient.phone, phoneCode)}
              </Typography>
              {patient.email && (
                <Typography variant="body2">
                  Email: {patient.email}
                </Typography>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSendMessageDialogClose}>Cancel</Button>
          <Button 
            onClick={handleSendMessage} 
            variant="contained" 
            startIcon={<SendIcon />}
            disabled={!newMessage.message.trim()}
          >
            Send
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={invoiceDeleteDialogOpen}
        title="Delete invoice"
        message="Are you sure you want to delete this invoice? This action cannot be undone."
        confirmText="Delete"
        confirmColor="error"
        loading={invoiceDeleteLoading}
        onClose={() => {
          if (!invoiceDeleteLoading) {
            setInvoiceDeleteDialogOpen(false);
            setPendingDeleteInvoiceId(null);
          }
        }}
        onConfirm={handleDeleteInvoice}
      />

    </Container>
  );
};

export default PatientDetails;